import React, { useEffect, useState } from "react";
import {
  ArrowBack,
  MailOutline,
  Phone,
  Edit,
  ContentCopy,
  Person,
  Pause,
  FavoriteBorder,
} from "@mui/icons-material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Typography,
  useTheme,
  Avatar,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  CircularProgress,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  ChangeMemberStatus,
  GetMemberProfilePictureUploadUrl,
  GetOneMember,
  MemberResponse,
  MemberSubscriptions,
  MemberSubscriptionsData,
  GetSubscriptionPauseHistory,
  PauseSubscription,
  SubscriptionPauseHistoryItem,
  ResumeSubscription,
  StartSubscription,
  UpdateMember,
  UpdateMemberProfilePicture,
  GetMemberNotes,
} from "../../services/MembersService";
import { sendPutRequest } from "../../services/ContentServices";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import AddIcon from "@mui/icons-material/Add";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CancelIcon from "@mui/icons-material/Cancel";
import { isValidEmail, validatePhoneNumber } from "../../utils/commons";
import dayjs from "dayjs";
import { AttendanceCalendar } from "../../components/AttendanceCalendar";
import { useSnackbar } from "notistack";
import MemberNotesDrawer from "./MemberNotes";
import DietLogsDrawer from "./DietLogsDrawer";
import RestaurantOutlinedIcon from "@mui/icons-material/RestaurantOutlined";
import DrawerAssignSubscription from "./DrawerSubscription";
import { CancelSubscriptionDialog } from "./CancelSubscriptionDialog";
import BodyMeasurementDrawer from "./BodyMeasurementDrawer";
import BodyMeasurementHistoryDrawer from "./BodyMeasurementHistoryDrawer";
import HistoryIcon from "@mui/icons-material/History";
import {
  BodyMeasurement,
  getLatestBodyMeasurement,
} from "../../services/BodyMeasurementService";

interface ViewMemberProps {
  setShowView: React.Dispatch<React.SetStateAction<boolean>>;
  selectedMember: string;
}

interface HealthData {
  height: number;
  weight: number;
}

interface MemberData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dob: string;
  memberType: "VISITOR" | "SUBSCRIBER" | "GYM_MEMBER" | "ONLINE_MEMBER";
  healthData: HealthData;
  gender: string;
  createdAt: string;
  updatedAt: string;
  photoUrl: string;
  registered: boolean;
  active: boolean;
  admin: boolean;
}

const ViewMember: React.FC<ViewMemberProps> = ({
  setShowView,
  selectedMember,
}) => {
  const theme = useTheme();
  const [mode, setMode] = useState<"view" | "edit">("view");
  const [canEdit, setCanEdit] = useState<boolean>(false);
  const [drawerNote, setDrawerNote] = useState<boolean>(false);
  const [dietLogsOpen, setDietLogsOpen] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [notesCount, setNotesCount] = useState<number>(0);
  const [isProfileDialogOpen, setIsProfileDialogOpen] =
    useState<boolean>(false);
  const [profilePictureFile, setProfilePictureFile] = useState<File | null>(
    null
  );
  const [profilePicturePreview, setProfilePicturePreview] =
    useState<string>("");
  const [isProfilePictureSaving, setIsProfilePictureSaving] =
    useState<boolean>(false);
  const [avatarCacheKey, setAvatarCacheKey] = useState<number>(Date.now());
  const { enqueueSnackbar } = useSnackbar();

  const [memberData, setMemberData] = useState<MemberData>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    dob: "",
    memberType: "VISITOR",
    healthData: { height: 0, weight: 0 },
    gender: "",
    createdAt: "",
    updatedAt: "",
    photoUrl: "",
    registered: false,
    active: false,
    admin: false,
  });

  const [initialData, setInitialData] = useState<{
    prevfName: string;
    prevlName: string;
    prevPhone: string;
    prevEmail: string;
    prevGender: string;
    prevHeight: number;
    prevWeight: number;
  }>({
    prevfName: "",
    prevlName: "",
    prevPhone: "",
    prevEmail: "",
    prevGender: "",
    prevHeight: 0,
    prevWeight: 0,
  });

  useEffect(() => {
    fetchData();
    fetchNotesCount();
  }, []);

  useEffect(() => {
    const isDisableChanges =
      memberData.firstName == initialData.prevfName &&
      memberData.lastName == initialData.prevlName &&
      memberData.email == initialData.prevEmail &&
      memberData.phone == initialData.prevPhone &&
      memberData.gender == initialData.prevGender;
    setCanEdit(isDisableChanges);
  }, [memberData, mode]);

  const resetForm = () => {
    setMemberData((prev) => ({
      ...prev,
      firstName: initialData.prevfName,
      lastName: initialData.prevlName,
      email: initialData.prevEmail,
      phone: initialData.prevPhone,
      gender: initialData.prevGender,
      healthData: {
        weight: initialData.prevWeight,
        height: initialData.prevHeight,
      },
    }));
  };

  const reloadMember = async () => {
    await fetchData();
  };

  const fetchNotesCount = async () => {
    try {
      const data = await GetMemberNotes({
        memberId: selectedMember,
        pageSize: 1,
        page: 1,
      });
      setNotesCount(data.totalElements ?? 0);
    } catch {
      setNotesCount(0);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const data: MemberResponse = await GetOneMember({ id: selectedMember });
      setMemberData({
        firstName: data.firstName as string,
        lastName: data.lastName as string,
        email: data.email as string,
        phone: data.phone as string,
        dob: data.dob || "",
        memberType: data.memberType as
          | "VISITOR"
          | "SUBSCRIBER"
          | "GYM_MEMBER"
          | "ONLINE_MEMBER",
        healthData: {
          height: data.healthData?.height ?? 0,
          weight: data.healthData?.weight ?? 0,
        },
        gender: data.gender as string,
        createdAt: data.createdAt as string,
        updatedAt: data.updatedAt as string,
        photoUrl: data.photoUrl as string,
        registered: data.registered as boolean,
        active: data.active as boolean,
        admin: data.admin as boolean,
      });
      setInitialData({
        prevfName: data.firstName as string,
        prevlName: data.lastName as string,
        prevPhone: data.phone as string,
        prevEmail: data.email as string,
        prevGender: data.gender as string,
        prevHeight: data.healthData?.height ?? 0,
        prevWeight: data.healthData?.weight ?? 0,
      });
    } catch (error) {
      console.error("Error fetching member data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveChanges = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!memberData.firstName) {
      return enqueueSnackbar("Please provide the firstName");
    }
    if (!memberData.lastName) {
      return enqueueSnackbar("Please provide the lastName");
    }
    if (memberData.phone && !validatePhoneNumber(memberData.phone)) {
      return enqueueSnackbar("Invalid PhoneNo");
    }
    if (!memberData.email && !isValidEmail(memberData.email)) {
      return enqueueSnackbar("Invalid Email");
    }
    if (!memberData.gender) {
      return enqueueSnackbar("Please select the Gender");
    }

    setIsUploading(true);
    const result = await UpdateMember({ ...memberData, id: selectedMember });
    setIsUploading(false);
    if (result == 200) {
      enqueueSnackbar("Member updated successfully");
      reloadMember();
      setMode("view");
    } else {
      enqueueSnackbar("Failed To update member", { variant: "error" });
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setMemberData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDeactivation = async () => {
    setIsUploading(true);
    const status = await ChangeMemberStatus({
      userId: selectedMember,
      isActive: false,
    });
    setIsUploading(false);
    if (status == 200) {
      reloadMember();
      enqueueSnackbar("Member Deactivated Successfully!!", {
        variant: "warning",
      });
    } else {
      enqueueSnackbar("Member Deactivation Failed!!", { variant: "error" });
    }
  };

  const handleActivation = async () => {
    setIsUploading(true);
    const status = await ChangeMemberStatus({
      userId: selectedMember,
      isActive: true,
    });
    setIsUploading(false);
    if (status == 200) {
      reloadMember();
      enqueueSnackbar("Member Activated Successfully!!", {
        variant: "success",
      });
    } else {
      enqueueSnackbar("Member Activation Failed!!", { variant: "error" });
    }
  };

  const handleCopyMemberId = async () => {
    try {
      await navigator.clipboard.writeText(selectedMember);
      enqueueSnackbar("User ID copied", { variant: "success" });
    } catch {
      enqueueSnackbar("Unable to copy User ID", { variant: "error" });
    }
  };

  const handleNoteClose = () => {
    setDrawerNote(false);
    fetchNotesCount();
  };

  const handleOpenProfileDialog = () => {
    setProfilePictureFile(null);
    setProfilePicturePreview(memberData.photoUrl || "");
    setIsProfileDialogOpen(true);
  };

  const handleCloseProfileDialog = () => {
    setProfilePictureFile(null);
    setProfilePicturePreview("");
    setIsProfileDialogOpen(false);
  };

  const handleProfilePictureSelect = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      enqueueSnackbar("Please upload a PNG, JPEG, or JPG image", {
        variant: "warning",
      });
      return;
    }

    setProfilePictureFile(file);
    setProfilePicturePreview(URL.createObjectURL(file));
  };

  const handleProfilePictureSave = async () => {
    if (!profilePictureFile) {
      enqueueSnackbar("Please select a profile picture first", {
        variant: "warning",
      });
      return;
    }

    try {
      setIsProfilePictureSaving(true);

      const signUrlResponse = await GetMemberProfilePictureUploadUrl({
        userId: selectedMember,
        fileName: profilePictureFile.name,
      });
      const signUrl = signUrlResponse?.data?.data as string;
      if (!signUrl) {
        throw new Error("Missing upload URL");
      }

      const uploadResponse = await sendPutRequest({
        signUrl,
        file: profilePictureFile,
      });

      if (uploadResponse.status !== 200) {
        throw new Error("Upload failed");
      }

      const photoUrl = signUrl.split("?")[0];
      const status = await UpdateMemberProfilePicture({
        userId: selectedMember,
        photoUrl,
      });

      if (status === 200) {
        setMemberData((prev) => ({ ...prev, photoUrl }));
        setAvatarCacheKey(Date.now());
        enqueueSnackbar("Profile picture updated successfully", {
          variant: "success",
        });
        handleCloseProfileDialog();
      } else {
        enqueueSnackbar("Failed to update profile picture", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar("Failed to upload profile picture", {
        variant: "error",
      });
    } finally {
      setIsProfilePictureSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  const isGymMember = memberData.memberType === "GYM_MEMBER";
  const leftColumnMd = mode === "edit" ? 4 : 3;
  let programsMd: number;
  if (mode === "edit") {
    programsMd = isGymMember ? 4 : 8;
  } else {
    programsMd = isGymMember ? 5 : 9;
  }

  return (
    <Box sx={{ padding: theme.spacing(2) }}>
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Box display="flex" alignItems="center">
          <IconButton
            edge="start"
            aria-label="back"
            onClick={() => {
              if (mode === "edit") {
                resetForm();
                setMode("view");
              } else {
                setShowView(false);
              }
            }}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <MemberHeader
            memberData={memberData}
            memberId={selectedMember}
            avatarCacheKey={avatarCacheKey}
            onEditAvatar={handleOpenProfileDialog}
            onCopyMemberId={handleCopyMemberId}
          />
        </Box>

        <Box display="flex" alignItems="center" gap={1.5}>
          {mode === "edit" ? (
            <>
              <Button variant="contained" color="primary" onClick={resetForm}>
                Reset
              </Button>
              <Button
                variant="outlined"
                onClick={handleSaveChanges}
                color="secondary"
                disabled={canEdit || isUploading}
              >
                {isUploading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Save"
                )}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<StickyNote2OutlinedIcon />}
                onClick={() => setDrawerNote(true)}
                sx={{ borderRadius: 2 }}
              >
                Notes
                {notesCount > 0 && (
                  <Chip
                    label={notesCount}
                    size="small"
                    sx={{
                      ml: 1,
                      height: 20,
                      minWidth: 20,
                      fontSize: "0.75rem",
                      backgroundColor: theme.palette.background.default,
                      color: theme.palette.text.primary,
                    }}
                  />
                )}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<RestaurantOutlinedIcon />}
                onClick={() => setDietLogsOpen(true)}
                sx={{ borderRadius: 2 }}
              >
                Diet Logs
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<Edit />}
                onClick={() => setMode("edit")}
                sx={{ borderRadius: 2 }}
              >
                Edit
              </Button>
              {memberData.active ? (
                <Button
                  variant="outlined"
                  color="error"
                  disabled={isUploading}
                  onClick={handleDeactivation}
                  sx={{ borderRadius: 2 }}
                >
                  Deactivate
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  color="secondary"
                  disabled={isUploading}
                  onClick={handleActivation}
                  sx={{ borderRadius: 2 }}
                >
                  Activate
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Body: three-column layout */}
      <Grid container spacing={3}>
        {/* Left column: health data (view, narrow) or profile editor (edit) */}
        <Grid item xs={12} md={leftColumnMd}>
          {mode === "edit" ? (
            <ProfileEditCard
              memberData={memberData}
              setMemberData={setMemberData}
              handleInputChange={handleInputChange}
            />
          ) : (
            <HealthDataBand memberData={memberData} memberId={selectedMember} />
          )}
        </Grid>

        {/* Programs — absorbs the width freed up by the narrower health card */}
        <Grid item xs={12} md={programsMd}>
          <UserProgramsCard memberId={selectedMember} />
        </Grid>

        {/* Attendance */}
        {isGymMember && (
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                borderRadius: 2,
                p: 2,
                height: "100%",
                backgroundColor: theme.palette.background.paper,
                boxShadow: "0 1px 6px rgba(0,0,0,0.4)",
              }}
            >
              <AttendanceCalendar memberId={selectedMember} />
            </Box>
          </Grid>
        )}
      </Grid>

      <MemberNotesDrawer
        open={drawerNote}
        memberId={selectedMember}
        onClose={handleNoteClose}
      />

      <DietLogsDrawer
        open={dietLogsOpen}
        userId={selectedMember}
        onClose={() => setDietLogsOpen(false)}
      />

      <Dialog
        open={isProfileDialogOpen}
        onClose={handleCloseProfileDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Update Profile Picture</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              mt: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Avatar
              src={
                profilePicturePreview ||
                buildAvatarUrl(memberData.photoUrl, avatarCacheKey)
              }
              sx={{
                width: 100,
                height: 100,
                bgcolor: theme.palette.secondary.main,
                fontSize: "1.8rem",
                fontWeight: 600,
              }}
            >
              {(memberData.firstName?.[0] || "")
                .concat(memberData.lastName?.[0] || "")
                .toUpperCase()}
            </Avatar>

            <Button variant="outlined" component="label">
              Choose Picture
              <input
                hidden
                type="file"
                accept=".png,.jpg,.jpeg"
                onChange={handleProfilePictureSelect}
              />
            </Button>

            <Typography variant="caption" color="text.secondary">
              {profilePictureFile
                ? profilePictureFile.name
                : "PNG, JPG or JPEG only"}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProfileDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleProfilePictureSave}
            disabled={!profilePictureFile || isProfilePictureSaving}
          >
            {isProfilePictureSaving ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              "Save"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export { ViewMember };

const buildAvatarUrl = (photoUrl: string, cacheKey: number) => {
  if (!photoUrl) {
    return "";
  }

  const separator = photoUrl.includes("?") ? "&" : "?";
  return `${photoUrl}${separator}v=${cacheKey}`;
};

/* ─── Member Header ──────────────────────────────────────────── */

const MemberHeader = ({
  memberData,
  memberId,
  avatarCacheKey,
  onEditAvatar,
  onCopyMemberId,
}: {
  memberData: MemberData;
  memberId: string;
  avatarCacheKey: number;
  onEditAvatar: () => void;
  onCopyMemberId: () => void;
}) => {
  const theme = useTheme();
  const initials =
    (memberData.firstName?.[0] || "") + (memberData.lastName?.[0] || "");

  return (
    <Box display="flex" alignItems="center" gap={2}>
      <Box
        sx={{
          position: "relative",
          "&:hover .member-avatar-edit": {
            opacity: 1,
            transform: "scale(1)",
          },
        }}
      >
        <Avatar
          src={buildAvatarUrl(memberData.photoUrl, avatarCacheKey)}
          sx={{
            width: 56,
            height: 56,
            bgcolor: theme.palette.secondary.main,
            fontSize: "1.2rem",
            fontWeight: 600,
          }}
        >
          {initials.toUpperCase()}
        </Avatar>
        <IconButton
          size="small"
          className="member-avatar-edit"
          onClick={onEditAvatar}
          sx={{
            position: "absolute",
            right: -6,
            bottom: -6,
            opacity: 0,
            transform: "scale(0.9)",
            transition: "all 0.2s ease",
            bgcolor: theme.palette.background.paper,
            border: `2px solid ${theme.palette.secondary.main}`,
            "&:hover": {
              bgcolor: theme.palette.background.default,
            },
          }}
        >
          <Edit sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
      <Box>
        <Typography variant="h6" fontWeight={600}>
          {memberData.firstName} {memberData.lastName}
        </Typography>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="caption" color="text.secondary">
            {memberId}
          </Typography>
          <IconButton
            size="small"
            onClick={onCopyMemberId}
            sx={{ p: 0.25, color: theme.palette.text.secondary }}
            aria-label="copy user id"
          >
            <ContentCopy sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
        {memberData.email && (
          <Box display="flex" alignItems="center" gap={0.5}>
            <MailOutline
              sx={{ fontSize: 14, color: theme.palette.text.secondary }}
            />
            <Typography variant="body2" color="text.secondary">
              {memberData.email}
            </Typography>
          </Box>
        )}
        {memberData.phone && (
          <Box display="flex" alignItems="center" gap={0.5}>
            <Phone sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
            <Typography variant="body2" color="text.secondary">
              {memberData.phone}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

/* ─── Health Data Band (latest body-measurement scan) ─────────── */

const HealthDataBand = ({
  memberData,
  memberId,
}: {
  memberData: MemberData;
  memberId: string;
}) => {
  const theme = useTheme();
  const [latest, setLatest] = useState<BodyMeasurement | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [historyOpen, setHistoryOpen] = useState<boolean>(false);

  const fetchLatest = async () => {
    try {
      setLoading(true);
      const result = await getLatestBodyMeasurement(memberId);
      setLatest(result);
    } catch {
      setLatest(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLatest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  const dobAge = getAgeFromDob(memberData.dob);

  // BMI comes from the scan only: its own value, else computed from the
  // scan's own height/weight. The profile healthData field is retired.
  const computedBmi =
    latest?.bmi ??
    (latest && latest.heightCm && latest.weightKg
      ? Number(
          (latest.weightKg / Math.pow(latest.heightCm / 100, 2)).toFixed(1)
        )
      : undefined);

  const fmt = (value?: number | null, unit?: string) =>
    value === undefined || value === null
      ? "—"
      : `${value}${unit ? ` ${unit}` : ""}`;

  type Tile = { label: string; value: string; tooltip?: string };
  const tiles: Tile[] = latest
    ? [
        { label: "Gender", value: memberData.gender || "—" },
        { label: "Age", value: dobAge ? `${dobAge} yrs` : "—" },
        { label: "Weight", value: fmt(latest.weightKg, "kg") },
        {
          label: "BMI",
          value: computedBmi !== undefined ? String(computedBmi) : "—",
        },
        { label: "Body fat", value: fmt(latest.bodyFatPct, "%") },
        { label: "Body fat", value: fmt(latest.bodyFatKg, "kg") },
        { label: "Skeletal muscle", value: fmt(latest.skeletalMuscleKg, "kg") },
        { label: "Muscle", value: fmt(latest.skeletalMusclePct, "%") },
        { label: "Visceral fat", value: fmt(latest.visceralFatLevel) },
        {
          label: "Body age",
          value:
            latest.bodyAge !== undefined && latest.bodyAge !== null
              ? `${latest.bodyAge} yrs`
              : "—",
        },
        {
          label: "RMR",
          value: fmt(latest.restingMetabolismKcal, "kcal"),
          tooltip:
            "Resting metabolic rate — the calories the body burns at rest in a day.",
        },
        { label: "Height", value: fmt(latest.heightCm, "cm") },
      ].filter((t) => t.value !== "—")
    : [
        { label: "Gender", value: memberData.gender || "—" },
        { label: "Age", value: dobAge ? `${dobAge} yrs` : "—" },
      ];

  return (
    <Box
      sx={{
        borderRadius: 2,
        p: 3,
        backgroundColor: theme.palette.background.paper,
        boxShadow: "0 1px 6px rgba(0,0,0,0.4)",
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        mb={0.5}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <FavoriteBorder
            sx={{ fontSize: 20, color: theme.palette.text.secondary }}
          />
          <Typography variant="h6">Health Data</Typography>
        </Box>
        <Button
          size="small"
          color="inherit"
          startIcon={<HistoryIcon />}
          onClick={() => setHistoryOpen(true)}
          sx={{ borderRadius: 2, color: theme.palette.text.secondary }}
        >
          History
        </Button>
      </Box>
      {latest && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1.5 }}
        >
          Measured {dayjs(latest.measuredAt).format("MMM D, YYYY")}
          {latest.measuredBy ? ` · by ${latest.measuredBy}` : ""}
        </Typography>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          {!latest && (
            <Typography variant="body2" color="text.secondary" mb={2}>
              No measurements recorded yet. Add the first measurement to start
              tracking body composition over time.
            </Typography>
          )}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 1,
            }}
          >
            {tiles.map((tile, i) => (
              <Box
                key={`${tile.label}-${i}`}
                sx={{
                  borderRadius: 2,
                  p: 1.25,
                  backgroundColor: theme.palette.background.default,
                }}
              >
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {tile.label}
                  </Typography>
                  {tile.tooltip && (
                    <Tooltip title={tile.tooltip} arrow>
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: 14,
                          color: theme.palette.text.secondary,
                          cursor: "help",
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  {tile.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}

      <Button
        variant="outlined"
        fullWidth
        startIcon={<AddIcon />}
        onClick={() => setDrawerOpen(true)}
        sx={{ borderRadius: 2, mt: 2 }}
      >
        Add measurement
      </Button>

      <BodyMeasurementDrawer
        open={drawerOpen}
        userId={memberId}
        onClose={() => setDrawerOpen(false)}
        onCreated={fetchLatest}
      />

      <BodyMeasurementHistoryDrawer
        open={historyOpen}
        userId={memberId}
        onClose={() => setHistoryOpen(false)}
        onChanged={fetchLatest}
      />
    </Box>
  );
};

/* ─── Profile Edit Card ──────────────────────────────────────── */

const ProfileEditCard = ({
  memberData,
  setMemberData,
  handleInputChange,
}: {
  memberData: MemberData;
  setMemberData: React.Dispatch<React.SetStateAction<MemberData>>;
  handleInputChange: (e: any) => void;
}) => {
  const theme = useTheme();

  // Edit mode
  return (
    <Box
      sx={{
        borderRadius: 2,
        p: 3,
        backgroundColor: theme.palette.background.paper,
        boxShadow: "0 1px 6px rgba(0,0,0,0.4)",
      }}
    >
      <Typography variant="h6" mb={2}>
        Edit Profile
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 0.5, color: theme.palette.text.secondary }}
          >
            First Name
          </Typography>
          <TextField
            fullWidth
            size="small"
            name="firstName"
            value={memberData.firstName || ""}
            onChange={handleInputChange}
            InputProps={{
              startAdornment: (
                <Person sx={{ mr: 1, color: theme.palette.text.secondary }} />
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 0.5, color: theme.palette.text.secondary }}
          >
            Last Name
          </Typography>
          <TextField
            fullWidth
            size="small"
            name="lastName"
            value={memberData.lastName || ""}
            onChange={handleInputChange}
            InputProps={{
              startAdornment: (
                <Person sx={{ mr: 1, color: theme.palette.text.secondary }} />
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 0.5, color: theme.palette.text.secondary }}
          >
            Email
          </Typography>
          <TextField
            fullWidth
            size="small"
            name="email"
            value={memberData.email || ""}
            onChange={handleInputChange}
            InputProps={{
              startAdornment: (
                <MailOutline
                  sx={{ mr: 1, color: theme.palette.text.secondary }}
                />
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 0.5, color: theme.palette.text.secondary }}
          >
            Phone
          </Typography>
          <TextField
            fullWidth
            size="small"
            name="phone"
            value={memberData.phone || ""}
            disabled
            InputProps={{
              startAdornment: (
                <Phone sx={{ mr: 1, color: theme.palette.text.secondary }} />
              ),
            }}
          />
        </Grid>
      </Grid>

      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        Gender
      </Typography>
      <RadioGroup
        row
        value={memberData.gender || ""}
        name="gender"
        onChange={handleInputChange}
        sx={{ mb: 2 }}
      >
        <FormControlLabel value="MALE" control={<Radio />} label="Male" />
        <FormControlLabel value="FEMALE" control={<Radio />} label="Female" />
        <FormControlLabel value="OTHER" control={<Radio />} label="Other" />
      </RadioGroup>
    </Box>
  );
};

/* ─── User Programs Card ─────────────────────────────────────── */

const getStatusColor = (status: string) => {
  switch (status) {
    case "ACTIVE":
      return "success";
    case "PAUSED":
      return "warning";
    case "EXPIRED":
      return "default";
    case "CANCELLED":
      return "error";
    default:
      return "info";
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case "ACTIVE":
      return "Active";
    case "PAUSED":
      return "Paused";
    case "EXPIRED":
      return "Completed";
    case "CANCELLED":
      return "Cancelled";
    default:
      return status;
  }
};

const getDaysRemaining = (endDate: string): number => {
  const end = dayjs(endDate);
  const today = dayjs();
  return end.diff(today, "day");
};

const formatSubDate = (dateStr: string) => {
  if (!dateStr) return "—";
  return dayjs(dateStr).format("MMM D, YYYY");
};

const getAgeFromDob = (dob?: string): number | null => {
  if (!dob) {
    return null;
  }

  const parsedDob = dayjs(dob);
  if (!parsedDob.isValid()) {
    return null;
  }

  const age = dayjs().diff(parsedDob, "year");
  return age >= 0 ? age : null;
};

const getPauseQuotaAvailable = (sub: MemberSubscriptionsData) => {
  const maxPausePeriodAllowed = sub.subscription?.maxPausePeriodAllowed || 0;
  const totalPausedDays = sub.totalPausedDays || 0;
  return Math.max(0, maxPausePeriodAllowed - totalPausedDays);
};

const extractErrorMessage = (error: any, fallback: string) => {
  return (
    error?.response?.data?.message || error?.response?.data?.error || fallback
  );
};

const PauseDetails = ({
  userSubscriptionId,
  availablePauseQuota,
  activePauseId,
}: {
  userSubscriptionId: string;
  availablePauseQuota: number;
  activePauseId?: string | null;
}) => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [pausedAt, setPausedAt] = useState<string>("");
  const [effectiveResumeDate, setEffectiveResumeDate] = useState<string>("");
  const [hasLoaded, setHasLoaded] = useState(false);

  const fetchPauseDetails = async () => {
    try {
      setIsLoading(true);
      const pauseHistory = await GetSubscriptionPauseHistory({
        userSubscriptionId,
        page: 0,
        size: 10,
      });
      const activePause =
        pauseHistory.content.find(
          (pause: SubscriptionPauseHistoryItem) =>
            pause.status === "ACTIVE" &&
            (!activePauseId || pause.id === activePauseId)
        ) ||
        pauseHistory.content.find(
          (pause: SubscriptionPauseHistoryItem) => pause.status === "ACTIVE"
        );

      if (!activePause?.pausedAt) {
        setPausedAt("");
        setEffectiveResumeDate("");
        return;
      }

      const pausedAtDate = dayjs(activePause.pausedAt);
      setPausedAt(
        pausedAtDate.isValid() ? formatSubDate(activePause.pausedAt) : ""
      );
      setEffectiveResumeDate(
        pausedAtDate.isValid()
          ? pausedAtDate.add(availablePauseQuota, "day").format("MMM D, YYYY")
          : ""
      );
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  };

  useEffect(() => {
    fetchPauseDetails();
  }, [userSubscriptionId, availablePauseQuota, activePauseId]);

  return (
    <Box sx={{ mt: 0.5 }}>
      <Typography variant="body2" color="warning.main" sx={{ mb: 0.5 }}>
        Paused on: {pausedAt || "—"}{" "}
        {availablePauseQuota
          ? `with Pause Quota of ${availablePauseQuota} days`
          : ""}
      </Typography>
      <Box display="flex" alignItems="center" gap={0.5}>
        <Typography variant="body2" color="warning.main">
          Auto Resumes On:{" "}
          {effectiveResumeDate || (hasLoaded ? "—" : "Loading...")}
        </Typography>
        <IconButton
          size="small"
          onClick={fetchPauseDetails}
          disabled={isLoading}
          sx={{ color: theme.palette.text.secondary, p: 0.25 }}
        >
          {isLoading ? (
            <CircularProgress size={12} />
          ) : (
            <RefreshIcon sx={{ fontSize: 14 }} />
          )}
        </IconButton>
      </Box>
    </Box>
  );
};

const UserProgramsCard = ({ memberId }: { memberId: string }) => {
  const theme = useTheme();
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState<
    MemberSubscriptionsData[]
  >([]);
  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false);
  const [pausingSubId, setPausingSubId] = useState<string | null>(null);
  const [resumingSubId, setResumingSubId] = useState<string | null>(null);
  const [startingSubId, setStartingSubId] = useState<string | null>(null);
  const [selectedSubscription, setSelectedSubscription] =
    useState<MemberSubscriptionsData | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      const result = await MemberSubscriptions({ id: memberId });
      setSubscriptionPlans(result);
    } catch {
      setSubscriptionPlans([]);
    }
  };

  const handleCancelSuccess = () => {
    fetchSubscriptions();
  };

  const handlePause = async (subscription: MemberSubscriptionsData) => {
    try {
      setPausingSubId(subscription.id);
      await PauseSubscription({
        userSubscriptionId: subscription.id,
        reason: "OTHER",
      });
      enqueueSnackbar("Subscription paused successfully", {
        variant: "success",
      });
      await fetchSubscriptions();
    } catch (error) {
      enqueueSnackbar(
        extractErrorMessage(error, "Failed to pause subscription"),
        {
          variant: "error",
        }
      );
    } finally {
      setPausingSubId(null);
    }
  };

  const handleResume = async (subscription: MemberSubscriptionsData) => {
    try {
      setResumingSubId(subscription.id);
      await ResumeSubscription({ userSubscriptionId: subscription.id });
      enqueueSnackbar("Subscription resumed successfully", {
        variant: "success",
      });
      await fetchSubscriptions();
    } catch (error) {
      enqueueSnackbar(
        extractErrorMessage(error, "Failed to resume subscription"),
        {
          variant: "error",
        }
      );
    } finally {
      setResumingSubId(null);
    }
  };

  const handleStart = async (subscription: MemberSubscriptionsData) => {
    try {
      setStartingSubId(subscription.id);
      await StartSubscription({ userSubscriptionId: subscription.id });
      enqueueSnackbar("Subscription started successfully", {
        variant: "success",
      });
      await fetchSubscriptions();
    } catch (error) {
      enqueueSnackbar(
        extractErrorMessage(error, "Failed to start subscription"),
        {
          variant: "error",
        }
      );
    } finally {
      setStartingSubId(null);
    }
  };

  const actionableSubs = subscriptionPlans.filter(
    (p) =>
      p.status === "ACTIVE" || p.status === "PAUSED" || p.status === "GRANTED"
  );
  const enrolledSubs = subscriptionPlans.filter(
    (p) =>
      p.status !== "ACTIVE" && p.status !== "PAUSED" && p.status !== "GRANTED"
  );

  return (
    <>
      <Box
        sx={{
          borderRadius: 2,
          p: 3,
          height: "100%",
          backgroundColor: theme.palette.background.paper,
          boxShadow: "0 1px 6px rgba(0,0,0,0.4)",
        }}
      >
        <Typography variant="h6" mb={2}>
          User Programs
        </Typography>

        {actionableSubs.map((sub) => {
          const pauseQuota = getPauseQuotaAvailable(sub);
          const isPausing = pausingSubId === sub.id;
          const isResuming = resumingSubId === sub.id;
          const isStarting = startingSubId === sub.id;

          return (
            <Box
              key={sub.id}
              sx={{
                border: `2px solid ${theme.palette.secondary.main}`,
                borderRadius: 2,
                p: 2,
                mb: 3,
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={1}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {sub.subscription?.name || "Subscription"}
                  </Typography>
                  <Chip
                    label={getStatusLabel(sub.status)}
                    size="small"
                    color={
                      getStatusColor(sub.status) as
                        | "success"
                        | "default"
                        | "error"
                        | "info"
                        | "warning"
                    }
                    sx={{ height: 22, fontSize: "0.75rem" }}
                  />
                </Box>
              </Box>

              {sub.status !== "GRANTED" && (
                <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                  <CalendarTodayIcon
                    sx={{ fontSize: 14, color: theme.palette.text.secondary }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {formatSubDate(sub.startDate)} →{" "}
                    {sub.subscription?.duration > 9999
                      ? "Lifetime"
                      : formatSubDate(sub.endDate)}
                  </Typography>
                </Box>
              )}

              {sub.status === "ACTIVE" && (
                <>
                  {sub.subscription?.duration <= 9999 && (
                    <Typography
                      variant="body2"
                      color="secondary"
                      sx={{ mb: 0.25 }}
                    >
                      {getDaysRemaining(sub.endDate)} days remaining
                    </Typography>
                  )}

                  <Typography
                    variant="body2"
                    color="warning.main"
                    sx={{ mb: 1.5 }}
                  >
                    {pauseQuota} days pause quota available
                  </Typography>

                  <Box display="flex" gap={1}>
                    <Button
                      size="small"
                      variant="contained"
                      color="warning"
                      startIcon={<Pause />}
                      onClick={() => handlePause(sub)}
                      disabled={isPausing || pauseQuota <= 0}
                      sx={{ borderRadius: 1.5, textTransform: "none" }}
                    >
                      {isPausing ? "Pausing..." : "Pause"}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="error"
                      startIcon={<CancelIcon />}
                      onClick={() => {
                        setSelectedSubscription(sub);
                        setCancelDialogOpen(true);
                      }}
                      sx={{ borderRadius: 1.5, textTransform: "none" }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </>
              )}

              {sub.status === "PAUSED" && (
                <>
                  <PauseDetails
                    userSubscriptionId={sub.id}
                    availablePauseQuota={pauseQuota}
                    activePauseId={sub.activePauseId}
                  />

                  <Box display="flex" gap={1} mt={1.5}>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<PlayArrowIcon />}
                      onClick={() => handleResume(sub)}
                      disabled={isResuming}
                      sx={{ borderRadius: 1.5, textTransform: "none" }}
                    >
                      {isResuming ? "Resuming..." : "Resume"}
                    </Button>
                  </Box>
                </>
              )}

              {sub.status === "GRANTED" && (
                <>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1.5 }}
                  >
                    Subscription is granted and waiting to start.
                  </Typography>

                  <Box display="flex" gap={1}>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<PlayArrowIcon />}
                      onClick={() => handleStart(sub)}
                      disabled={isStarting}
                      sx={{ borderRadius: 1.5, textTransform: "none" }}
                    >
                      {isStarting ? "Starting..." : "Start"}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="error"
                      startIcon={<CancelIcon />}
                      onClick={() => {
                        setSelectedSubscription(sub);
                        setCancelDialogOpen(true);
                      }}
                      sx={{ borderRadius: 1.5, textTransform: "none" }}
                    >
                      Revoke
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          );
        })}

        {/* Enrolled Programs header */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Enrolled Programs
          </Typography>
          <Button
            size="small"
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={() => setDrawerOpen(true)}
            sx={{ borderRadius: 1.5, textTransform: "none" }}
          >
            Assign Program
          </Button>
        </Box>

        {/* Enrolled list */}
        <Box
          sx={{
            maxHeight: "45vh",
            overflow: "auto",
            scrollbarWidth: "none",
          }}
        >
          {enrolledSubs.length === 0 && actionableSubs.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              py={4}
            >
              No programs assigned yet
            </Typography>
          )}
          {enrolledSubs.map((plan) => (
            <Box
              key={plan.id}
              sx={{
                borderRadius: 2,
                p: 2,
                mb: 1.5,
                backgroundColor: "rgba(255,255,255,0.03)",
                boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={0.5}
              >
                <Typography variant="body1" fontWeight={500}>
                  {plan.subscription?.name || "Subscription"}
                </Typography>
                <Chip
                  label={getStatusLabel(plan.status)}
                  size="small"
                  color={
                    getStatusColor(plan.status) as
                      | "success"
                      | "default"
                      | "error"
                      | "info"
                      | "warning"
                  }
                  variant="outlined"
                  sx={{ height: 22, fontSize: "0.7rem" }}
                />
              </Box>
              <Box display="flex" alignItems="center" gap={0.5}>
                <CalendarTodayIcon
                  sx={{ fontSize: 13, color: theme.palette.text.secondary }}
                />
                <Typography variant="caption" color="text.secondary">
                  {formatSubDate(plan.startDate)} →{" "}
                  {plan.subscription?.duration > 9999
                    ? "Lifetime"
                    : formatSubDate(plan.endDate)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <DrawerAssignSubscription
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onUpdate={fetchSubscriptions}
        memberId={memberId}
      />
      <CancelSubscriptionDialog
        open={cancelDialogOpen}
        onClose={() => {
          setCancelDialogOpen(false);
          setSelectedSubscription(null);
        }}
        onSuccess={handleCancelSuccess}
        subscription={selectedSubscription}
      />
    </>
  );
};
